//Built-in libraries

//Third party libraries
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Bars4Icon,
  BriefcaseIcon,
  ChartBarIcon,
  FunnelIcon,
  MapIcon,
  PresentationChartBarIcon,
  ServerIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { type ChangeEvent, useState } from 'react';
import { useNavigate } from 'react-router';
import ProjectFilterModal from '../../../Projects/ProjectFilterModal';
//Custom components
import Select from '../../../UI/Form/Select';

//Services

//STP Libraries
import { Stages as ProjectStages, Status as ProjectStatuses } from '@stptech/shared-kit/build/constants/project';
import ProjectSortModal from '@/components/Projects/ProjectSortModal';
//Assets andd Miscellaneous
import { projectType } from '../../../../constants/Project';
import type { FiltersInterface } from '../../../../types/Miscellaneous';
import type { TProjectStages } from '../../../../types/Project';
import { isObjectEmpty } from '../../../../utils';
import { FilterBadgesList } from '../../../FilterBadgesList';

const allProjectStatuses = Object.keys(ProjectStatuses).reduce((acc: any, next) => {
  const stageStatus = ProjectStatuses[next as TProjectStages];
  Object.keys(stageStatus).forEach((sk) => {
    acc[sk] = stageStatus[sk as keyof typeof stageStatus];
  });
  return acc;
}, {});

//Component Begin
interface ProjectsListControlsInterface {
  projectView: string;
  appliedFilters: FiltersInterface;
  handleFiltersChange: (filters: FiltersInterface) => void;
  projectViewChangeHandler: (view: string) => void;
}

const ProjectsListControls = ({
  appliedFilters,
  projectView,
  handleFiltersChange,
  projectViewChangeHandler,
}: ProjectsListControlsInterface) => {
  const [showModal, setShowModal] = useState(false);
  const [showSortModal, setShowSortModal] = useState(false);
  const [displayFilters, setDisplayFilters] = useState<{
    [key: string]: any;
  }>({});
  const navigate = useNavigate();

  const viewFilterHandler = () => {
    setShowModal((prevState) => !prevState);
  };

  const filterChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
    if (event.target.value.toUpperCase() === 'ALL') {
      const updatedFilters = { ...appliedFilters };
      delete updatedFilters[event.target.name];

      const updatedDisplayFilters = { ...displayFilters };
      delete updatedDisplayFilters[event.target.name];

      handleFiltersChange(updatedFilters);
      setDisplayFilters(updatedDisplayFilters);
      return;
    }

    handleFiltersChange({
      ...appliedFilters,
      [event.target.name]: event.target.value,
    });

    let displayValue = {
      label: event.target.name,
      value: event.target.value,
    };

    if (event.target.name === 'status') {
      displayValue = {
        label: 'Status',
        value: allProjectStatuses[event.target.value],
      };
    }

    if (event.target.name === 'stage') {
      displayValue = {
        label: 'Stage',
        value: ProjectStages[event.target.value as TProjectStages],
      };
    }

    if (event.target.name === 'type') {
      displayValue = {
        label: 'Project Type',
        value: event.target.value,
      };
    }

    setDisplayFilters((prevState) => ({
      ...prevState,
      [event.target.name]: displayValue,
    }));
  };

  const removeFilterHandler = (key: string) => {
    const updatedAppliedFilters = { ...appliedFilters };
    const updatedDisplayFilters = { ...displayFilters };

    delete updatedAppliedFilters[key];
    delete updatedDisplayFilters[key];

    handleFiltersChange(updatedAppliedFilters);
    setDisplayFilters(updatedDisplayFilters);
  };

  const applyFiltersHandler = (filters: FiltersInterface) => {
    handleFiltersChange({
      ...appliedFilters,
      ...filters,
    });
  };

  const displayFiltersHandler = (filters: any) => {
    setDisplayFilters((prevState) => ({ ...prevState, ...filters }));
  };

  let availableProjectStatuses = allProjectStatuses;

  if (appliedFilters.stage) {
    availableProjectStatuses = ProjectStatuses[appliedFilters.stage as TProjectStages];
  }

  return (
    <>
      {showModal && (
        <ProjectFilterModal
          handleModal={viewFilterHandler}
          applyFiltersHandler={applyFiltersHandler}
          handleDisplayFilters={displayFiltersHandler}
        />
      )}
      {showSortModal && (
        <ProjectSortModal
          handleModal={() => setShowSortModal((prevState) => !prevState)}
          applyFiltersHandler={applyFiltersHandler}
          handleDisplayFilters={displayFiltersHandler}
        />
      )}
      <div className='flex flex-col lg:flex-row justify-between items-center gap-2 text-neutralColor-normal items-end'>
        <section id='project-list-filters' className='flex flex-col md:flex-row items-center gap-4 text-sm'>
          <div className='bg-white space-y-2 border p-2 rounded w-full'>
            <div className='flex gap-2 items-center'>
              <ChartBarIcon className='h-5 w-5' />
              <span>Project Stage</span>
            </div>
            <Select
              id='filter-project-stage'
              name='stage'
              className='mr-4 bg-white w-full'
              value={appliedFilters.stage ? appliedFilters.stage : 'all'}
              onChange={filterChangeHandler}
            >
              <option value='all'>All</option>
              {Object.keys(ProjectStages).map((key) => {
                return (
                  <option key={key} value={key}>
                    {ProjectStages[key as TProjectStages]}
                  </option>
                );
              })}
            </Select>
          </div>
          <div className='bg-white space-y-2 border p-2 rounded w-full'>
            <div className='flex gap-2 items-center'>
              <ServerIcon className='h-5 w-5' />
              <span>Project Status</span>
            </div>
            <Select
              id='filter-project-status'
              name='status'
              className='mr-4 bg-white w-full'
              value={appliedFilters.status ? appliedFilters.status : 'all'}
              onChange={filterChangeHandler}
            >
              <option value='all'>All</option>
              {Object.keys(availableProjectStatuses).map((key: string) => {
                return (
                  <option key={key} value={key}>
                    {availableProjectStatuses[key]}
                  </option>
                );
              })}
            </Select>
          </div>
          <div className='bg-white space-y-2 border p-2 rounded w-full'>
            <div className='flex gap-2 items-center'>
              <BriefcaseIcon className='h-5 w-5' />
              <span>Project Type</span>
            </div>
            <Select
              id='filter-project-type'
              name='type'
              className='mr-4 bg-white w-full'
              value={appliedFilters.type ? appliedFilters.type : 'all'}
              onChange={filterChangeHandler}
            >
              <option value='all'>All</option>
              {projectType.map((res) => (
                <option key={res.value} value={res.value}>
                  {res.value}
                </option>
              ))}
            </Select>
          </div>
          <div
            className='w-full py-2 flex flex-col gap-2 justify-center items-center rounded cursor-pointer  bg-neutralColor-lighter hover:bg-neutralColor-light transition duration-200 ease-in-out'
            onClick={viewFilterHandler}
          >
            <FunnelIcon className='h-5 w-5' />
            <span>More Filter</span>
          </div>
          <div
            className='w-full py-2 flex flex-col gap-2 justify-center items-center rounded cursor-pointer  bg-neutralColor-lighter hover:bg-neutralColor-light transition duration-200 ease-in-out'
            onClick={() => setShowSortModal(true)}
          >
            <div className='flex'>
              <ArrowDownIcon className='h-4 w-4' />
              <ArrowUpIcon className='h-4 w-4' />
            </div>
            <span>Sort</span>
          </div>
          <div
            className='w-full py-2 flex flex-col gap-2 justify-center items-center rounded cursor-pointer  bg-neutralColor-lighter hover:bg-neutralColor-light transition duration-200 ease-in-out'
            onClick={() => {
              navigate('reports');
            }}
          >
            <div className='flex'>
              <PresentationChartBarIcon className='size-5' />
            </div>
            <span>Reports</span>
          </div>
        </section>
        <section id='project-view'>
          <label className='text-xs'>Project View</label>
          <dl className='flex mt-2 gap-2 text-center'>
            <div>
              <dt
                className={`p-2 rounded-md cursor-pointer hover:bg-primaryColor-normal hover:text-white transition duration-200 ease-in-out ${
                  projectView === 'list' ? 'bg-primaryColor-normal text-white' : 'bg-white'
                }`}
                onClick={() => projectViewChangeHandler('list')}
              >
                <Bars4Icon className='h-5 w-5' />
              </dt>
              <dd className='text-xs'>List</dd>
            </div>
            <div>
              <dt
                className={`p-2 rounded-md cursor-pointer hover:bg-primaryColor-normal hover:text-white transition duration-200 ease-in-out ${
                  projectView === 'map' ? 'bg-primaryColor-normal text-white' : 'bg-white'
                }`}
                onClick={() => projectViewChangeHandler('map')}
              >
                <MapIcon className='h-5 w-5' />
              </dt>
              <dd className='text-xs'>Map</dd>
            </div>
            <div>
              <dt
                className={`p-2 rounded-md cursor-pointer hover:bg-primaryColor-normal hover:text-white transition duration-200 ease-in-out ${
                  projectView === 'card' ? 'bg-primaryColor-normal text-white' : 'bg-white'
                }`}
                onClick={() => projectViewChangeHandler('card')}
              >
                <Squares2X2Icon className='h-5 w-5' />
              </dt>
              <dd className='text-xs'>Grid</dd>
            </div>
          </dl>
        </section>
      </div>
      {!isObjectEmpty(displayFilters) && <FilterBadgesList displayFilters={displayFilters} handleRemoveFilter={removeFilterHandler} />}
    </>
  );
};

export default ProjectsListControls;
